import { useState, useEffect } from "react";
import { useAuth, API, authFetch } from "@/App";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import PageLayout, { PageHeader } from "@/components/PageLayout";
import { toast } from "sonner";
import { 
  User, LogOut, FileText, Shield, Bell, ChevronRight, Heart, PawPrint, 
  CalendarCheck, Sun, Moon, Monitor, Palette, ArrowLeft, Smartphone, Copy, Check, Eye, EyeOff, Loader2, Settings as SettingsIcon, Briefcase
} from "lucide-react";

// Theme management
const THEME_KEY = "zampa-theme";

const getSystemTheme = () => {
  if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
    return 'dark';
  }
  return 'light';
};

const applyTheme = (theme) => {
  const root = document.documentElement;
  const effectiveTheme = theme === 'system' ? getSystemTheme() : theme;
  
  if (effectiveTheme === 'dark') {
    root.classList.add('dark');
  } else {
    root.classList.remove('dark');
  }
};

export default function Settings() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [theme, setTheme] = useState(() => {
    return localStorage.getItem(THEME_KEY) || 'system';
  });
  const [showThemeOptions, setShowThemeOptions] = useState(false);
  
  // 2FA states
  const [twoFAStatus, setTwoFAStatus] = useState(null);
  const [show2FASetup, setShow2FASetup] = useState(false);
  const [show2FADisable, setShow2FADisable] = useState(false);
  const [setupData, setSetupData] = useState(null);
  const [loading2FA, setLoading2FA] = useState(false);
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [showSecret, setShowSecret] = useState(false);
  const [copied, setCopied] = useState(false);
  
  // User preferences state
  const [hideProfessionalBanner, setHideProfessionalBanner] = useState(false);
  const [loadingPrefs, setLoadingPrefs] = useState(false);
  
  // Fetch user preferences
  useEffect(() => {
    const fetchPrefs = async () => {
      try {
        const res = await authFetch(`${API}/user/preferences`);
        if (res.ok) {
          const data = await res.json();
          setHideProfessionalBanner(data.hide_professional_banner || false);
        }
      } catch (e) {}
    };
    fetchPrefs();
  }, []);
  
  // Toggle professional banner preference
  const toggleProfessionalBanner = async () => {
    setLoadingPrefs(true);
    try {
      const newValue = !hideProfessionalBanner;
      const res = await authFetch(`${API}/user/preferences`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ hide_professional_banner: newValue })
      });
      if (res.ok) {
        setHideProfessionalBanner(newValue);
        toast.success(newValue ? "Banner nascosto" : "Banner riattivato");
      }
    } catch (e) {
      toast.error("Errore nel salvataggio");
    } finally {
      setLoadingPrefs(false);
    }
  };
  
  // Fetch 2FA status on mount
  useEffect(() => {
    const fetch2FAStatus = async () => {
      try {
        const token = localStorage.getItem('session_token');
        const response = await fetch(`${API}/auth/2fa-status`, {
          headers: { 'Authorization': `Bearer ${token}` },
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          setTwoFAStatus(data);
        }
      } catch (error) {
        console.error("Error fetching 2FA status:", error);
      }
    };
    fetch2FAStatus();
  }, []);

  useEffect(() => {
    applyTheme(theme);
    localStorage.setItem(THEME_KEY, theme);

    // Listen for system theme changes
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      if (theme === 'system') {
        applyTheme('system');
      }
    };
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [theme]);

  const handleLogout = async () => {
    await logout();
    toast.success("Logout effettuato");
    navigate("/", { replace: true });
  };

  // 2FA Functions
  const start2FASetup = async () => {
    setLoading2FA(true);
    try {
      const token = localStorage.getItem('session_token');
      const response = await fetch(`${API}/auth/enable-2fa`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setSetupData(data);
        setShow2FASetup(true);
        setCode(['', '', '', '', '', '']);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Errore nell'attivazione del 2FA");
      }
    } catch (error) {
      toast.error("Errore di connessione");
    } finally {
      setLoading2FA(false);
    }
  };

  const confirm2FA = async (codeValue) => {
    setLoading2FA(true);
    try {
      const token = localStorage.getItem('session_token');
      const response = await fetch(`${API}/auth/confirm-2fa`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ code: codeValue })
      });
      if (response.ok) {
        toast.success("2FA attivato con successo!");
        setShow2FASetup(false);
        setSetupData(null);
        setTwoFAStatus({ ...twoFAStatus, totp_enabled: true });
      } else {
        const error = await response.json();
        toast.error(error.detail || "Codice non valido");
        setCode(['', '', '', '', '', '']);
      }
    } catch (error) {
      toast.error("Errore di connessione");
    } finally {
      setLoading2FA(false);
    }
  };

  const disable2FA = async (codeValue) => {
    setLoading2FA(true);
    try {
      const token = localStorage.getItem('session_token');
      const response = await fetch(`${API}/auth/disable-2fa`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ code: codeValue })
      });
      if (response.ok) {
        toast.success("2FA disattivato");
        setShow2FADisable(false);
        setTwoFAStatus({ ...twoFAStatus, totp_enabled: false });
      } else {
        const error = await response.json();
        toast.error(error.detail || "Codice non valido");
        setCode(['', '', '', '', '', '']);
      }
    } catch (error) {
      toast.error("Errore di connessione");
    } finally {
      setLoading2FA(false);
    }
  };

  const handleCodeChange = (index, value, forDisable = false) => {
    if (value && !/^\d$/.test(value)) return;
    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    
    // Auto-focus next input
    if (value && index < 5) {
      const nextInput = document.querySelector(`[data-2fa-index="${index + 1}"]`);
      if (nextInput) nextInput.focus();
    }
    
    // Auto-submit when complete
    if (value && index === 5) {
      const fullCode = newCode.join('');
      if (fullCode.length === 6) {
        if (forDisable) {
          disable2FA(fullCode);
        } else {
          confirm2FA(fullCode);
        }
      }
    }
  };
  
  const handleCodeKeyDown = (index, e, forDisable = false) => {
    // Handle backspace - move to previous input
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      const prevInput = document.querySelector(`[data-2fa-index="${index - 1}"]`);
      if (prevInput) prevInput.focus();
    }
  };
  
  const handleCodePaste = (e, forDisable = false) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length > 0) {
      const newCode = [...code];
      for (let i = 0; i < pasted.length && i < 6; i++) {
        newCode[i] = pasted[i];
      }
      setCode(newCode);
      
      // Auto-submit if 6 digits pasted
      if (pasted.length === 6) {
        if (forDisable) {
          disable2FA(pasted);
        } else {
          confirm2FA(pasted);
        }
      } else {
        // Focus the next empty input
        const nextInput = document.querySelector(`[data-2fa-index="${pasted.length}"]`);
        if (nextInput) nextInput.focus();
      }
    }
  };

  const copySecret = async () => {
    if (setupData?.totp_secret) {
      await navigator.clipboard.writeText(setupData.totp_secret);
      setCopied(true);
      toast.success("Codice copiato!");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const themeOptions = [
    { value: 'light', label: 'Chiaro', icon: Sun },
    { value: 'dark', label: 'Scuro', icon: Moon },
    { value: 'system', label: 'Sistema', icon: Monitor }
  ];

  const currentThemeOption = themeOptions.find(t => t.value === theme);

  const menuItems = [
    {
      icon: CalendarCheck,
      label: "Prenotazioni",
      description: "I tuoi appuntamenti",
      color: "#2563EB",
      onClick: () => navigate('/bookings')
    },
    {
      icon: User,
      label: "Profilo",
      description: "Gestisci i tuoi dati",
      color: "#E07A5F",
      onClick: () => navigate('/profile')
    },
    {
      icon: Bell,
      label: "Notifiche",
      description: "Imposta promemoria vaccini",
      color: "#F2CC8F",
      onClick: () => navigate('/notifications')
    },
    {
      icon: Shield,
      label: "Privacy",
      description: "I tuoi dati e diritti",
      color: "#81B29A",
      onClick: () => navigate('/privacy')
    },
    {
      icon: FileText,
      label: "Documenti",
      description: "Tutti i tuoi certificati",
      color: "#3D405B",
      onClick: () => navigate("/documents")
    }
  ];

  // Admin menu items (for admin users - for now show to all logged users)
  const adminMenuItems = [
    {
      icon: User,
      label: "Gestione Professionisti",
      description: "Approva e gestisci richieste",
      color: "#FF6B4A",
      onClick: () => navigate("/admin/professionals")
    }
  ];

  return (
    <PageLayout data-testid="settings-page">
      <PageHeader 
        title="Impostazioni"
        subtitle="Gestisci il tuo account"
        icon={SettingsIcon}
        iconColor="#FF6B4A"
      />
      
      {/* User Card */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 border border-black/5 dark:border-white/10 mb-6 shadow-sm">
        <div className="flex items-center gap-4">
          <div className="relative">
            {user?.picture ? (
              <img 
                src={user.picture} 
                alt={user.name}
                className="w-16 h-16 rounded-2xl object-cover"
              />
            ) : (
              <div className="w-16 h-16 rounded-2xl bg-[#FF6B4A]/10 flex items-center justify-center">
                <User className="text-[#FF6B4A]" size={28} />
              </div>
            )}
            {/* Verified badge for Google/OAuth users or confirmed email */}
            {(user?.auth_provider === 'google' || user?.email_verified) && (
              <div 
                className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center border-2 border-white dark:border-gray-800"
                title="Account verificato"
                data-testid="verified-user-badge"
              >
                <Check className="text-white" size={14} strokeWidth={3} />
              </div>
            )}
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h2 className="font-semibold text-[#1D1D1F] dark:text-white text-lg">{user?.name || "Utente"}</h2>
              {(user?.auth_provider === 'google' || user?.email_verified) && (
                <span className="text-xs bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-400 px-2 py-0.5 rounded-full font-medium">
                  Verificato
                </span>
              )}
            </div>
            <p className="text-[#86868B] dark:text-gray-400 text-sm">{user?.email}</p>
            {user?.auth_provider === 'google' && (
              <p className="text-xs text-[#86868B] dark:text-gray-500 mt-0.5">Accesso con Google</p>
            )}
          </div>
        </div>
      </div>

      {/* Theme Selector */}
      <div className="mb-6">
        <h3 className="text-sm font-semibold text-[#86868B] dark:text-gray-400 uppercase tracking-wider mb-3 px-1">
          Aspetto
        </h3>
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-black/5 dark:border-white/10 shadow-sm overflow-hidden">
          <button
            onClick={() => setShowThemeOptions(!showThemeOptions)}
            className="w-full p-4 flex items-center gap-4 text-left"
            data-testid="theme-selector"
          >
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-100 to-indigo-100 dark:from-amber-900/30 dark:to-indigo-900/30 flex items-center justify-center">
              <Palette className="text-indigo-600 dark:text-indigo-400" size={20} />
            </div>
            <div className="flex-1">
              <p className="font-medium text-[#1D1D1F] dark:text-white">Tema</p>
              <p className="text-sm text-[#86868B] dark:text-gray-400 flex items-center gap-1">
                {currentThemeOption && <currentThemeOption.icon size={14} />}
                {currentThemeOption?.label}
              </p>
            </div>
            <ChevronRight className={`text-[#86868B] transition-transform ${showThemeOptions ? 'rotate-90' : ''}`} size={20} />
          </button>
          
          {showThemeOptions && (
            <div className="border-t border-black/5 dark:border-white/10 p-2">
              <div className="flex gap-2">
                {themeOptions.map((option) => {
                  const Icon = option.icon;
                  const isSelected = theme === option.value;
                  return (
                    <button
                      key={option.value}
                      onClick={() => {
                        setTheme(option.value);
                        setShowThemeOptions(false);
                        toast.success(`Tema ${option.label.toLowerCase()} attivato`);
                      }}
                      className={`flex-1 p-3 rounded-xl flex flex-col items-center gap-2 transition-all ${
                        isSelected 
                          ? 'bg-[#FF6B4A] text-white' 
                          : 'bg-[#F5F5F7] dark:bg-gray-700 text-[#1D1D1F] dark:text-white hover:bg-[#EBEBEB] dark:hover:bg-gray-600'
                      }`}
                      data-testid={`theme-${option.value}`}
                    >
                      <Icon size={20} />
                      <span className="text-xs font-medium">{option.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Security - 2FA Section */}
      <div className="mb-6">
        <h3 className="text-sm font-semibold text-[#86868B] dark:text-gray-400 uppercase tracking-wider mb-3 px-1">
          Sicurezza
        </h3>
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-black/5 dark:border-white/10 shadow-sm overflow-hidden">
          <div className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-green-100 to-emerald-100 dark:from-green-900/30 dark:to-emerald-900/30 flex items-center justify-center">
              <Smartphone className="text-emerald-600 dark:text-emerald-400" size={20} />
            </div>
            <div className="flex-1">
              <p className="font-medium text-[#1D1D1F] dark:text-white">Autenticazione a due fattori</p>
              <p className="text-sm text-[#86868B] dark:text-gray-400">
                {twoFAStatus?.totp_enabled 
                  ? "Attivo - Protezione aggiuntiva abilitata" 
                  : "Non attivo - Proteggi il tuo account"
                }
              </p>
            </div>
            {twoFAStatus?.is_admin && twoFAStatus?.totp_enabled ? (
              <span className="px-3 py-1 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 text-xs font-medium rounded-full">
                Obbligatorio
              </span>
            ) : twoFAStatus?.totp_enabled ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setCode(['', '', '', '', '', '']);
                  setShow2FADisable(true);
                }}
                className="text-red-500 border-red-200 hover:bg-red-50"
                data-testid="disable-2fa-btn"
              >
                Disattiva
              </Button>
            ) : (
              <Button
                size="sm"
                onClick={start2FASetup}
                disabled={loading2FA}
                className="bg-[#FF6B4A] hover:bg-[#E05A3A]"
                data-testid="enable-2fa-btn"
              >
                {loading2FA ? <Loader2 className="w-4 h-4 animate-spin" /> : "Attiva"}
              </Button>
            )}
          </div>
        </div>
      </div>
      
      {/* Professional Settings */}
      <div className="mb-6">
        <h3 className="text-sm font-semibold text-[#86868B] dark:text-gray-400 uppercase tracking-wider mb-3 px-1">
          Professionisti
        </h3>
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-black/5 dark:border-white/10 shadow-sm overflow-hidden">
          <div className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#1D3557]/10 to-[#457B9D]/10 dark:from-[#1D3557]/30 dark:to-[#457B9D]/30 flex items-center justify-center">
              <Briefcase className="text-[#1D3557] dark:text-[#457B9D]" size={20} />
            </div>
            <div className="flex-1">
              <p className="font-medium text-[#1D1D1F] dark:text-white">Banner "Sei un professionista?"</p>
              <p className="text-sm text-[#86868B] dark:text-gray-400">
                {hideProfessionalBanner 
                  ? "Nascosto - Riattivalo per registrarti come professionista" 
                  : "Visibile nella sezione Servizi"
                }
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={toggleProfessionalBanner}
              disabled={loadingPrefs}
              className={hideProfessionalBanner 
                ? "bg-[#FF6B4A] text-white hover:bg-[#E55A3A] border-[#FF6B4A]" 
                : "text-[#86868B] border-gray-200"
              }
              data-testid="toggle-professional-banner-btn"
            >
              {loadingPrefs ? <Loader2 className="w-4 h-4 animate-spin" /> : (hideProfessionalBanner ? "Riattiva" : "Nascondi")}
            </Button>
          </div>
        </div>
      </div>
      
      {/* Menu Items */}
      <div className="space-y-2 mb-6">
        <h3 className="text-sm font-semibold text-[#86868B] dark:text-gray-400 uppercase tracking-wider mb-3 px-1">
          Account
        </h3>
        {menuItems.map((item, index) => (
          <button
            key={index}
            onClick={item.onClick}
            className="w-full bg-white dark:bg-gray-800 rounded-2xl p-4 border border-black/5 dark:border-white/10 flex items-center gap-4 hover:shadow-sm transition-all text-left shadow-sm"
            data-testid={`settings-${item.label.toLowerCase()}`}
          >
            <div 
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: `${item.color}15` }}
            >
              <item.icon style={{ color: item.color }} size={20} />
            </div>
            <div className="flex-1">
              <p className="font-medium text-[#1D1D1F] dark:text-white">{item.label}</p>
              <p className="text-sm text-[#86868B] dark:text-gray-400">{item.description}</p>
            </div>
            <ChevronRight className="text-[#86868B] dark:text-gray-500" size={20} />
          </button>
        ))}
      </div>
      
      {/* Admin Section - Only for admins */}
      {user?.is_admin && (
        <div className="space-y-2 mb-6">
          <h3 className="text-sm font-semibold text-[#86868B] dark:text-gray-400 uppercase tracking-wider mb-3 px-1">
            Amministrazione
          </h3>
          {adminMenuItems.map((item, index) => (
            <button
              key={index}
              onClick={item.onClick}
              className="w-full bg-white dark:bg-gray-800 rounded-2xl p-4 border border-black/5 dark:border-white/10 flex items-center gap-4 hover:shadow-sm transition-all text-left shadow-sm"
            >
              <div 
                className="w-10 h-10 rounded-xl flex items-center justify-center"
                style={{ backgroundColor: `${item.color}15` }}
              >
                <item.icon style={{ color: item.color }} size={20} />
              </div>
              <div className="flex-1">
                <p className="font-medium text-[#1D1D1F] dark:text-white">{item.label}</p>
                <p className="text-sm text-[#86868B] dark:text-gray-400">{item.description}</p>
              </div>
              <ChevronRight className="text-[#86868B] dark:text-gray-500" size={20} />
            </button>
          ))}
        </div>
      )}
      
      {/* About Section */}
      <div className="bg-gradient-to-br from-[#FF6B4A]/10 to-[#FF8F6B]/10 dark:from-[#FF6B4A]/20 dark:to-[#FF8F6B]/20 rounded-2xl p-5 mb-6">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-xl bg-[#FF6B4A] flex items-center justify-center shadow-lg shadow-[#FF6B4A]/30">
            <PawPrint className="text-white" size={22} />
          </div>
          <div>
            <h3 className="font-semibold text-[#1D1D1F] dark:text-white">Zampa</h3>
            <p className="text-xs text-[#86868B] dark:text-gray-400">Versione 1.0.0</p>
          </div>
        </div>
        <p className="text-sm text-[#6E6E73] dark:text-gray-300 mb-3">
          L'app essenziale per prenderti cura dei tuoi animali domestici. Vaccini, documenti e veterinari sempre a portata di mano.
        </p>
        <div className="flex items-center gap-1 text-sm text-[#6E6E73] dark:text-gray-300">
          <span>Fatto con</span>
          <Heart className="text-[#FF6B4A] fill-[#FF6B4A]" size={14} />
          <span>per chi ama gli animali</span>
        </div>
      </div>
      
      {/* Logout Button */}
      <Button
        onClick={handleLogout}
        variant="outline"
        className="w-full rounded-xl py-5 border-red-200 dark:border-red-900 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 mb-24"
        data-testid="logout-btn"
      >
        <LogOut size={18} className="mr-2" />
        Esci dall'account
      </Button>

      {/* 2FA Setup Dialog */}
      <Dialog open={show2FASetup} onOpenChange={(open) => {
        if (!open) {
          setShow2FASetup(false);
          setSetupData(null);
          setCode(['', '', '', '', '', '']);
        }
      }}>
        <DialogContent className="max-w-[420px] rounded-3xl bg-white dark:bg-gray-900 p-0 border-0">
          <div className="p-6">
            <DialogHeader className="text-center mb-6">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-400 to-green-500 flex items-center justify-center mx-auto mb-4">
                <Smartphone className="text-white" size={32} />
              </div>
              <DialogTitle className="text-2xl font-bold text-[#1D1D1F] dark:text-white">
                Attiva 2FA
              </DialogTitle>
              <p className="text-[#86868B] mt-2">
                Scansiona il QR code con la tua app di autenticazione
              </p>
            </DialogHeader>

            {setupData?.qr_code && (
              <div className="mb-6">
                <div className="flex justify-center mb-4">
                  <div className="p-4 bg-white rounded-2xl shadow-sm border border-gray-100">
                    <img src={setupData.qr_code} alt="QR Code" className="w-48 h-48" />
                  </div>
                </div>

                <div className="bg-[#F5F5F7] dark:bg-gray-800 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-[#86868B]">Oppure inserisci manualmente:</span>
                    <button onClick={() => setShowSecret(!showSecret)} className="text-[#FF6B4A]">
                      {showSecret ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 text-xs font-mono bg-white dark:bg-gray-700 p-2 rounded-lg overflow-x-auto">
                      {showSecret ? setupData.totp_secret : '••••••••••••••••••••••••••••••••'}
                    </code>
                    <button onClick={copySecret} className="p-2 hover:bg-white dark:hover:bg-gray-700 rounded-lg">
                      {copied ? <Check size={16} className="text-green-500" /> : <Copy size={16} className="text-[#86868B]" />}
                    </button>
                  </div>
                </div>

                <div className="mt-4 flex items-center justify-center gap-4 text-xs text-[#86868B]">
                  <span className="flex items-center gap-1"><Smartphone size={14} /> Google Authenticator</span>
                  <span>•</span><span>Authy</span>
                </div>
              </div>
            )}

            <div className="mb-4">
              <label className="block text-sm font-medium text-[#1D1D1F] dark:text-white mb-2 text-center">
                Inserisci il codice per confermare:
              </label>
              <div className="flex justify-center gap-2" onPaste={(e) => handleCodePaste(e, false)}>
                {code.map((digit, index) => (
                  <Input
                    key={index}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleCodeChange(index, e.target.value, false)}
                    onKeyDown={(e) => handleCodeKeyDown(index, e, false)}
                    data-2fa-index={index}
                    data-testid={`2fa-setup-input-${index}`}
                    className="w-12 h-14 text-center text-2xl font-bold rounded-xl bg-[#F5F5F7] dark:bg-gray-800 border-0 focus:ring-2 focus:ring-emerald-500"
                    disabled={loading2FA}
                  />
                ))}
              </div>
            </div>

            <Button
              onClick={() => confirm2FA(code.join(''))}
              disabled={loading2FA || code.join('').length !== 6}
              className="w-full h-14 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white text-lg font-semibold"
            >
              {loading2FA ? <Loader2 className="w-6 h-6 animate-spin" /> : "Attiva 2FA"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* 2FA Disable Dialog */}
      <Dialog open={show2FADisable} onOpenChange={(open) => {
        if (!open) {
          setShow2FADisable(false);
          setCode(['', '', '', '', '', '']);
        }
      }}>
        <DialogContent className="max-w-[380px] rounded-3xl bg-white dark:bg-gray-900 p-0 border-0">
          <div className="p-6">
            <DialogHeader className="text-center mb-6">
              <div className="w-16 h-16 rounded-2xl bg-red-100 dark:bg-red-900/30 flex items-center justify-center mx-auto mb-4">
                <Shield className="text-red-500" size={32} />
              </div>
              <DialogTitle className="text-2xl font-bold text-[#1D1D1F] dark:text-white">
                Disattiva 2FA
              </DialogTitle>
              <p className="text-[#86868B] mt-2">
                Inserisci il codice attuale per confermare
              </p>
            </DialogHeader>

            <div className="mb-4">
              <div className="flex justify-center gap-2" onPaste={(e) => handleCodePaste(e, true)}>
                {code.map((digit, index) => (
                  <Input
                    key={index}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleCodeChange(index, e.target.value, true)}
                    onKeyDown={(e) => handleCodeKeyDown(index, e, true)}
                    data-2fa-index={index}
                    data-testid={`2fa-disable-input-${index}`}
                    className="w-12 h-14 text-center text-2xl font-bold rounded-xl bg-[#F5F5F7] dark:bg-gray-800 border-0 focus:ring-2 focus:ring-red-500"
                    disabled={loading2FA}
                  />
                ))}
              </div>
            </div>

            <Button
              onClick={() => disable2FA(code.join(''))}
              disabled={loading2FA || code.join('').length !== 6}
              variant="destructive"
              className="w-full h-14 rounded-xl text-lg font-semibold"
            >
              {loading2FA ? <Loader2 className="w-6 h-6 animate-spin" /> : "Disattiva 2FA"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </PageLayout>
  );
}
